import { ReactPortal, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Typography } from '../Typography/Typography';
import { merge } from '../util';
import { Position } from './types';
import { constrainTooltipToViewport, getArrowTransform } from './util';

export type TooltipProps = {
  position?: Position;
  target: HTMLElement | null;
  text?: string;
  html?: string;
  title?: string;
  modalCompatibility?: boolean;
  interactive?: boolean;
};

export type TooltipContentProps = {
  position: Position;
  text?: string;
  html?: string;
  title?: string;
  interactive?: boolean;
  arrowTransform?: string;
};

const getTooltipContentMargin = (position: Position) => {
  if (position === 'bottom') {
    return 'mt-[-1px]';
  }
  if (position === 'top') {
    return 'mb-[-1px]';
  }
  if (position === 'right') {
    return 'mr-[-1px]';
  }
  if (position === 'left') {
    return 'ml-[-1px]';
  }
};

// offsets arrow position bridge gap between tooltip and target
const getPositionOffset = (position: Position) => {
  if (position === 'bottom') {
    return 'mt-[-3px]';
  }
  if (position === 'top') {
    return 'mb-[-3px]';
  }
  if (position === 'right') {
    return 'ml-[-3px]';
  }
  if (position === 'left') {
    return 'mr-[-3px]';
  }
};

// transform-origin is a valid svg attribute but v17 of react doesn't recognize it.
const transformOriginCenter = { 'transform-origin': 'center' };

const Triangle = ({
  transform,
  arrowTransform,
}: {
  transform?: string;
  arrowTransform?: string;
}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="7"
      viewBox="0 0 11 7"
      fill="none"
      overflow="visible"
      style={arrowTransform ? { transform: arrowTransform } : undefined}
    >
      <path
        d="M5.5 0L11 7L0 7L5.5 0Z"
        fill="var(--color-background-darker)"
        transform={transform}
        {...(transform ? transformOriginCenter : null)}
      />
    </svg>
  );
};

const TooltipContent = (props: TooltipContentProps) => {
  const { position, text, html, title } = props;

  return (
    <div
      className={merge(
        'kds flex flex-col items-center justify-center gap-10 bg-background-darker rounded-[3px] p-spacer-10 max-w-[min(600px,70vw)]',
        getTooltipContentMargin(position)
      )}
    >
      {title && (
        <Typography
          truncate
          color="font-white"
          size="sm"
          className="text-center max-w-[480px]"
        >
          {title}
        </Typography>
      )}
      {html ? (
        <div
          className="text-font-white text-[12px] [&_*]:text-[12px]"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <Typography color="font-white" size="sm" className="text-center">
          {text}
        </Typography>
      )}
    </div>
  );
};

// const BottomLeft = ({ position, text, title }: TooltipContentProps) => {
//   return (
//     <div className="flex flex-col w-fit">
//       <Triangle transform="translate(10)" />
//       <TooltipContent position={position} text={text} title={title} />
//     </div>
//   );
// };

const BottomMiddle = ({
  position,
  text,
  html,
  title,
  interactive,
  arrowTransform,
}: TooltipContentProps) => {
  return (
    <div
      className={merge(
        'flex flex-col w-fit items-center',
        interactive && getPositionOffset(position)
      )}
    >
      <Triangle arrowTransform={arrowTransform} />
      <TooltipContent
        position={position}
        text={text}
        html={html}
        title={title}
      />
    </div>
  );
};

// const BottomRight = ({ position, text, title }: TooltipContentProps) => {
//   return (
//     <div className="flex flex-col w-fit items-end">
//       <Triangle transform="translate(-10)" />
//       <TooltipContent position={position} text={text} title={title} />
//     </div>
//   );
// };

// const TopLeft = ({ position, text, title }: TooltipContentProps) => {
//   return (
//     <div className="flex flex-col w-fit">
//       <TooltipContent position={position} text={text} title={title} />
//       <Triangle transform="translate(10) rotate(180)" />
//     </div>
//   );
// };

const TopMiddle = ({
  position,
  text,
  html,
  title,
  interactive,
  arrowTransform,
}: TooltipContentProps) => {
  return (
    <div
      className={merge(
        'flex flex-col w-fit items-center',
        interactive && getPositionOffset(position)
      )}
    >
      <TooltipContent
        position={position}
        text={text}
        html={html}
        title={title}
      />
      <Triangle transform="rotate(180)" arrowTransform={arrowTransform} />
    </div>
  );
};

// const TopRight = ({ position, text, title }: TooltipContentProps) => {
//   return (
//     <div className="flex flex-col w-fit items-end">
//       <TooltipContent position={position} text={text} title={title} />
//       <Triangle transform="translate(-10) rotate(180)" />
//     </div>
//   );
// };

// const RightTop = ({ position, text, title }: TooltipContentProps) => {
//   return (
//     <div className="flex w-fit">
//       <Triangle transform="translate(2 7) rotate(270)" />
//       <TooltipContent position={position} text={text} title={title} />
//     </div>
//   );
// };

const RightMiddle = ({
  position,
  text,
  html,
  title,
  interactive,
  arrowTransform,
}: TooltipContentProps) => {
  return (
    <div
      className={merge(
        'flex w-fit items-center',
        interactive && getPositionOffset(position)
      )}
    >
      <Triangle
        transform="translate(2) rotate(270)"
        arrowTransform={arrowTransform}
      />
      <TooltipContent
        position={position}
        text={text}
        html={html}
        title={title}
      />
    </div>
  );
};

// const RightBottom = ({ position, text, title }: TooltipContentProps) => {
//   return (
//     <div className="flex w-fit items-end">
//       <Triangle transform="translate(2 -7) rotate(270)" />
//       <TooltipContent position={position} text={text} title={title} />
//     </div>
//   );
// };

// const LeftTop = ({ position, text, title }: TooltipContentProps) => {
//   return (
//     <div className="flex w-fit">
//       <TooltipContent position={position} text={text} title={title} />
//       <Triangle transform="translate(-2 7) rotate(90)" />
//     </div>
//   );
// };

const LeftMiddle = ({
  position,
  text,
  html,
  title,
  interactive,
  arrowTransform,
}: TooltipContentProps) => {
  return (
    <div
      className={merge(
        'flex w-fit items-center',
        interactive && getPositionOffset(position)
      )}
    >
      <TooltipContent
        position={position}
        text={text}
        html={html}
        title={title}
      />
      <Triangle
        transform="translate(-2) rotate(90)"
        arrowTransform={arrowTransform}
      />
    </div>
  );
};

// const LeftBottom = ({ position, text, title }: TooltipContentProps) => {
//   return (
//     <div className="flex w-fit items-end">
//       <TooltipContent position={position} text={text} title={title} />
//       <Triangle transform="translate(-2 -7) rotate(90)" />
//     </div>
//   );
// };

export const Tooltip = (props: TooltipProps): ReactPortal => {
  const {
    position = 'bottom',
    target,
    text,
    html,
    title,
    modalCompatibility,
    interactive,
  } = props;
  const tooltip = useRef<HTMLDivElement>(null);
  const [location, setLocation] = useState({ top: -1, left: -1 });
  const [arrowTransform, setArrowTransform] = useState<string>();
  useLayoutEffect(() => {
    const tooltipRect = tooltip.current?.getBoundingClientRect();
    const targetRect = target?.getBoundingClientRect();

    if (!tooltipRect || !targetRect) {
      return;
    }

    const { top, left } = constrainTooltipToViewport(
      position,
      tooltipRect,
      targetRect
    );

    setLocation({
      top: top + window.scrollY,
      left: left + window.scrollX,
    });
  }, [position, target]);

  // Measured after placement: near a viewport edge the box can re-wrap to a
  // different width than it had while hidden, so the first measurement is stale.
  useLayoutEffect(() => {
    const tooltipRect = tooltip.current?.getBoundingClientRect();
    const targetRect = target?.getBoundingClientRect();

    if (!tooltipRect || !targetRect || location.top === -1) {
      return;
    }

    setArrowTransform(getArrowTransform(position, tooltipRect, targetRect));
  }, [location, position, target]);

  const style =
    location.top === -1 && location.left === -1
      ? { transform: 'translate(-10000px, -10000px)' }
      : location;

  return createPortal(
    // This needs a high z-index becase of the floating frame compatability. The app supports
    // up to 5 nested modals and floating windows - see react-app/src/ts-components/Plugins/FLoatingWindows.tsx
    <div
      ref={tooltip}
      style={style}
      className={merge('absolute', modalCompatibility ? 'z-[105024]' : 'z-[2]')}
    >
      {position === 'bottom' && (
        <BottomMiddle
          position={position}
          text={text}
          html={html}
          title={title}
          interactive={interactive}
          arrowTransform={arrowTransform}
        />
      )}
      {position === 'top' && (
        <TopMiddle
          position={position}
          text={text}
          html={html}
          title={title}
          interactive={interactive}
          arrowTransform={arrowTransform}
        />
      )}
      {position === 'right' && (
        <RightMiddle
          position={position}
          text={text}
          html={html}
          title={title}
          interactive={interactive}
          arrowTransform={arrowTransform}
        />
      )}
      {position === 'left' && (
        <LeftMiddle
          position={position}
          text={text}
          html={html}
          title={title}
          interactive={interactive}
          arrowTransform={arrowTransform}
        />
      )}
    </div>,
    document.body
  );
};
